import React from 'react'
import Popover from '@mui/material/Popover'

import { alertHoverMocks } from './__mocks__/alertHover.mock'
import { AlertHoverCard } from './AlertHoverCard'
import { AlertHoverLoader } from './AlertHoverLoader'
import { alertHoverStyles } from './AlertHover.styles'
import type { AlertHoverPayload } from './alertHover.types'
import type { AlertSystemType, AlertSystemItem } from '../../config/alertSystem.types'

type Props = {
  anchorEl: HTMLElement | null
  alertId: string | null
  onClose: () => void
  alertType?: AlertSystemType | null
  alertData?: AlertSystemItem | null
}

export const resolveHoverData = (
  alertId: string | null,
  alertType?: AlertSystemType | null,
  alertData?: AlertSystemItem | null
): AlertHoverPayload | null => {
  // For simple alerts (requiresFetch = false), create payload from alertData
  if (alertData && !alertData.requiresFetch && alertData.data) {
    // Build simple alert payload based on type - matching the mock structure
    switch (alertType) {
      case 'payment':
        // Payment body: { failed: number, outstanding: number }
        return {
          id: alertData.clientId, // Use clean clientId for future API calls
          type: alertType,
          body: {
            failed: alertData.data.failed || 0,
            outstanding: alertData.data.outstanding || 0,
          },
        }
      case 'forms':
        // Forms body: { pendingCount: number }
        return {
          id: alertData.clientId,
          type: alertType,
          body: {
            pendingCount: alertData.data.count || 0,
          },
        }
      case 'notesProps':
        // NotesProps body: { notes: number, properties: number }
        return {
          id: alertData.clientId,
          type: alertType,
          body: {
            notes: alertData.data.notes || 0,
            properties: alertData.data.properties || 0,
          },
        }
      case 'suspended':
        return {
          id: alertData.clientId,
          type: alertType,
          body: {
            reason: 'suspended.defaultReason',
          },
        }
      case 'daysAbsent':
        return {
          id: alertData.clientId,
          type: alertType,
          body: { daysAbsent: alertData.data.daysAbsent ?? 0 },
          colorsOverride: alertData.colorsOverride,
        }
      case 'birthday':
        // Birthday body: { date: string }
        const dob = alertData.data.dateOfBirth
        let formattedDate = 'Date of birth on file'
        if (dob) {
          try {
            // Parse YYYY-MM-DD by splitting to avoid UTC→local timezone shift
            const parts = dob.split('-')
            if (parts.length === 3) {
              formattedDate = `${parts[2]}/${parts[1]}/${parts[0]}`
            }
          } catch {
            formattedDate = dob
          }
        }
        return {
          id: alertData.clientId,
          type: alertType,
          body: {
            date: formattedDate,
          },
        }
      case 'location':
        // Location body: { homeSiteName?: string } — the client's home/original site name
        return {
          id: alertData.clientId,
          type: alertType,
          body: {
            homeSiteName: alertData.data.homeSiteName ?? undefined,
          },
        }
    }
  }

  // For complex alerts (requiresFetch = true) or fallback, use mocks for now
  // TODO: Replace with actual API call for packages, agreements, introOffers
  if (alertId && alertHoverMocks[alertId]) {
    return alertHoverMocks[alertId]
  }

  if (alertType && alertHoverMocks[alertType]) {
    return alertHoverMocks[alertType]
  }

  return null
}

export const AlertHoverPopover: React.FC<Props> = ({
  anchorEl,
  alertId,
  onClose,
  alertType,
  alertData,
}) => {
  const open = Boolean(anchorEl) && Boolean(alertId)

  const [loading, setLoading] = React.useState(false)
  const [data, setData] = React.useState<AlertHoverPayload | null>(null)

  React.useEffect(() => {
    if (!alertId || !open) {
      setData(null)
      setLoading(false)
      return
    }

    // For simple alerts with data already available, show immediately
    if (alertData && !alertData.requiresFetch) {
      const simpleData = resolveHoverData(alertId, alertType, alertData)
      setData(simpleData)
      setLoading(false)
      return
    }

    // For complex alerts (requiresFetch = true), show loading and fetch data
    setLoading(true)
    setData(null)

    // TODO: Replace with real API call for complex alerts (packages, agreements, introOffers)
    // For now, use mock delay
    const timer = setTimeout(() => {
      const mock = resolveHoverData(alertId, alertType, alertData)
      setData(mock)
      setLoading(false)
    }, 350)

    return () => clearTimeout(timer)
  }, [alertId, alertType, alertData, open])

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      disableRestoreFocus
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={alertHoverStyles.popover}
    >
      {loading && <AlertHoverLoader />}
      {!loading && data && <AlertHoverCard data={data} />}
    </Popover>
  )
}
